import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { OrderType, UpdateOrderType } from '@klareospace/pharos-lib-types';
import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { createOrderTypeApi, orderTypeKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UpdateOrderTypeVariables {
  data: UpdateOrderType;
  id: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateOrderType(): UseMutationResult<OrderType, Error, UpdateOrderTypeVariables> {
  const client = useApiClient('admin-auth');
  const api = createOrderTypeApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<OrderType, Error, UpdateOrderTypeVariables>({
    mutationFn: ({ id, data }: UpdateOrderTypeVariables) => api.updateOrderType(id, data),
    ...createMutationCallbacks({
      invalidateKeys: [orderTypeKeys.all],
      queryClient,
      successKey: 'orderTypes.actions.edit.success',
      t,
    }),
  });
}
